import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { tripService } from '../../services/trip.service';
import { vehicleService } from '../../services/vehicle.service';
import { driverService } from '../../services/driver.service';
import { routeService } from '../../services/route.service';
import { contractService } from '../../services/contract.service';
import { vehicleExpenseService } from '../../services/vehicleExpense.service';
import { ContractStatus, ExpenseCategory } from '../../types';
import { geocodingService, LocationSuggestion } from '../../services/geocoding.service';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { LocationAutocomplete } from '../../components/ui/LocationAutocomplete';
import toast from 'react-hot-toast';

// Type for pending expenses to be created after trip
interface PendingExpense {
  id: string;
  category: ExpenseCategory;
  amount: number;
  description?: string;
  vendor?: string;
}

const tripSchema = z.object({
  vehicleId: z.number().positive('Vehicle is required'),
  driverId: z.number().positive('Driver is required'),
  routeId: z.number().optional(),
  contractId: z.number().optional(),
  origin: z.string().min(1, 'Origin is required'),
  destination: z.string().min(1, 'Destination is required'),
  purpose: z.string().min(1, 'Purpose is required'),
  jobReference: z.string().optional(),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().optional(),
  plannedDistanceKm: z
    .preprocess(
      (value) => (value === '' || value === null ? undefined : value),
      z.number().positive('Planned distance must be positive')
    )
    .optional(),
  weightInTons: z
    .preprocess(
      (value) => (value === '' || value === null ? undefined : value),
      z.number().positive('Weight must be positive')
    )
    .optional(),
  ratePerKm: z
    .preprocess(
      (value) => (value === '' || value === null ? undefined : value),
      z.number().positive('Rate per km must be positive')
    )
    .optional(),
  ratePerTon: z
    .preprocess(
      (value) => (value === '' || value === null ? undefined : value),
      z.number().positive('Rate per ton must be positive')
    )
    .optional(),
  amount: z
    .preprocess(
      (value) => (value === '' || value === null ? undefined : value),
      z.number().positive('Amount must be positive')
    )
    .optional(),
  fixedPrice: z
    .preprocess(
      (value) => (value === '' || value === null ? undefined : value),
      z.number().positive('Fixed price must be positive')
    )
    .optional(),
});

type TripFormData = z.input<typeof tripSchema>;

export const CreateTripPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [linkToContract, setLinkToContract] = useState(false);
  const [originLocation, setOriginLocation] = useState<LocationSuggestion | null>(null);
  const [destLocation, setDestLocation] = useState<LocationSuggestion | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [rateType, setRateType] = useState<'km' | 'ton' | 'fixed' | null>(null);
  
  // Pending expenses state
  const [pendingExpenses, setPendingExpenses] = useState<PendingExpense[]>([]);
  const [newExpenseCategory, setNewExpenseCategory] = useState<ExpenseCategory>(ExpenseCategory.FUEL);
  const [newExpenseAmount, setNewExpenseAmount] = useState('');
  const [newExpenseDescription, setNewExpenseDescription] = useState('');
  const [newExpenseVendor, setNewExpenseVendor] = useState('');

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', 'active'],
    queryFn: vehicleService.getActive,
  });

  const { data: drivers } = useQuery({
    queryKey: ['drivers', 'active'],
    queryFn: driverService.getActive,
  });

  const { data: routes } = useQuery({
    queryKey: ['routes', 'active'],
    queryFn: routeService.getActive,
  });

  const { data: activeContracts } = useQuery({
    queryKey: ['contracts', 'active'],
    queryFn: async () => {
      const all = await contractService.getAll();
      return all.filter(c => c.status === ContractStatus.ACTIVE || c.status === ContractStatus.IN_PROGRESS);
    },
  });

  const selectedContract = activeContracts?.find(c => c.id === selectedContractId);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<TripFormData>({
    resolver: zodResolver(tripSchema),
    defaultValues: {
      startDate: new Date().toISOString().split('T')[0],
    },
  });

  const origin = watch('origin');
  const destination = watch('destination');
  const plannedDistanceKm = watch('plannedDistanceKm');
  const weightInTons = watch('weightInTons');
  const ratePerKm = watch('ratePerKm');
  const ratePerTon = watch('ratePerTon');

  React.useEffect(() => {
    const distance = Number(plannedDistanceKm) || 0;
    const weight = Number(weightInTons) || 0;
    const kmRate = Number(ratePerKm) || 0;
    const tonRate = Number(ratePerTon) || 0;

    let calculatedAmount = 0;
    if (rateType === 'km' && kmRate && distance) {
      calculatedAmount = kmRate * distance;
    } else if (rateType === 'ton' && tonRate && weight) {
      calculatedAmount = tonRate * weight;
    }

    if (calculatedAmount > 0) {
      setValue('amount', Number(calculatedAmount.toFixed(2)));
    }
  }, [plannedDistanceKm, weightInTons, ratePerKm, ratePerTon, rateType, setValue]);

  const handleRateTypeChange = (type: 'km' | 'ton' | 'fixed') => {
    setRateType(type);
    if (type === 'km') {
      setValue('ratePerTon', undefined);
      setValue('fixedPrice', undefined);
    } else if (type === 'ton') {
      setValue('ratePerKm', undefined);
      setValue('fixedPrice', undefined);
    } else if (type === 'fixed') {
      setValue('ratePerKm', undefined);
      setValue('ratePerTon', undefined);
      setValue('amount', undefined);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (tripData: any) => {
      // Check if route exists for origin/destination, create if not
      const existingRoute = routes?.find(
        r => r.origin.toLowerCase() === tripData.origin.toLowerCase() && 
             r.destination.toLowerCase() === tripData.destination.toLowerCase()
      );
      
      let routeId = tripData.routeId;
      
      if (!existingRoute && tripData.origin && tripData.destination) {
        try {
          // Auto-create route
          const newRoute = await routeService.create({
            origin: tripData.origin,
            destination: tripData.destination,
            officialDistanceKm: tripData.plannedDistanceKm || 0,
            roundTrip: false,
          });
          routeId = newRoute.id;
          toast.success(`Route "${tripData.origin} → ${tripData.destination}" created automatically`);
        } catch (err) {
          console.warn('Failed to auto-create route, continuing without route:', err);
        }
      } else if (existingRoute && !routeId) {
        routeId = existingRoute.id;
      }
      
      return tripService.create({ ...tripData, routeId });
    },
    onSuccess: async (data) => {
      // Create pending expenses for the trip
      if (pendingExpenses.length > 0) {
        try {
          for (const expense of pendingExpenses) {
            await vehicleExpenseService.recordExpense({
              vehicleId: data.vehicleId,
              tripId: data.id,
              expenseDate: new Date().toISOString().split('T')[0],
              category: expense.category,
              amount: expense.amount,
              description: expense.description,
              vendor: expense.vendor,
            });
          }
          toast.success(`Trip created with ${pendingExpenses.length} expense(s)`);
        } catch (err) {
          toast.success('Trip created, but some expenses failed to save');
        }
      } else {
        toast.success('Trip created successfully');
      }
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      navigate(`/trips/${data.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create trip');
    },
  });

  const handleAddExpense = () => {
    if (!newExpenseAmount || parseFloat(newExpenseAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setPendingExpenses([
      ...pendingExpenses,
      {
        id: Date.now().toString(),
        category: newExpenseCategory,
        amount: parseFloat(newExpenseAmount),
        description: newExpenseDescription || undefined,
        vendor: newExpenseVendor || undefined,
      },
    ]);
    setNewExpenseAmount('');
    setNewExpenseDescription('');
    setNewExpenseVendor('');
  };

  const handleRemoveExpense = (id: string) => {
    setPendingExpenses(pendingExpenses.filter((e) => e.id !== id));
  };

  const expenseCategoryLabels: Record<ExpenseCategory, string> = {
    [ExpenseCategory.FUEL]: 'Fuel',
    [ExpenseCategory.MAINTENANCE]: 'Maintenance',
    [ExpenseCategory.REPAIR]: 'Repair',
    [ExpenseCategory.INSURANCE]: 'Insurance',
    [ExpenseCategory.LATRA]: 'LATRA',
    [ExpenseCategory.REGISTRATION]: 'Registration',
    [ExpenseCategory.PARKING]: 'Parking',
    [ExpenseCategory.TOLLS]: 'Tolls',
    [ExpenseCategory.CLEANING]: 'Cleaning',
    [ExpenseCategory.TIRES]: 'Tires',
    [ExpenseCategory.PARTS]: 'Parts',
    [ExpenseCategory.LABOR]: 'Labor',
    [ExpenseCategory.INSPECTION]: 'Inspection',
    [ExpenseCategory.FINES]: 'Fines',
    [ExpenseCategory.DRIVER_ALLOWANCE]: 'Driver Allowance',
    [ExpenseCategory.ACCOMMODATION]: 'Accommodation',
    [ExpenseCategory.MEALS]: 'Meals',
    [ExpenseCategory.TOLL_ROAD]: 'Toll/Road',
    [ExpenseCategory.LOADING_OFFLOADING]: 'Loading/Offloading',
    [ExpenseCategory.WEIGHBRIDGE]: 'Weighbridge',
    [ExpenseCategory.BORDER_FEES]: 'Border Fees',
    [ExpenseCategory.FUEL_ADVANCE]: 'Fuel Advance',
    [ExpenseCategory.OTHER]: 'Other',
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const calculateDistance = async () => {
    if (!originLocation || !destLocation) return;

    setIsCalculating(true);
    try {
      const result = await geocodingService.calculateDistance(
        originLocation.lat,
        originLocation.lon,
        destLocation.lat,
        destLocation.lon
      );

      if (result) {
        setValue('plannedDistanceKm', result.distanceKm);
        toast.success(`Distance calculated: ${result.distanceKm} km`);
      } else {
        toast.error('Could not calculate distance. Please enter manually.');
      }
    } catch (error) {
      toast.error('Failed to calculate distance');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleOriginSelect = async (location: LocationSuggestion) => {
    setOriginLocation(location);
  };

  const handleDestSelect = async (location: LocationSuggestion) => {
    setDestLocation(location);
  };

  const handleRouteChange = (routeId: string) => {
    const id = parseInt(routeId);
    if (id && routes) {
      const route = routes.find((r) => r.id === id);
      if (route) {
        setValue('origin', route.origin);
        setValue('destination', route.destination);
        setValue('plannedDistanceKm', route.officialDistanceKm);
        setValue('routeId', id);
        setSelectedRouteId(id);
        setOriginLocation(null);
        setDestLocation(null);
      }
    } else {
      setValue('routeId', undefined);
      setSelectedRouteId(null);
    }
  };

  const handleContractChange = (contractId: string) => {
    const id = parseInt(contractId);
    if (id && activeContracts) {
      const contract = activeContracts.find((c) => c.id === id);
      if (contract) {
        setValue('origin', contract.origin);
        setValue('destination', contract.destination);
        setValue('contractId', id);
        setSelectedContractId(id);
        setOriginLocation(null);
        setDestLocation(null);
        toast.success('Contract details auto-filled');
      }
    } else {
      setValue('contractId', undefined);
      setSelectedContractId(null);
    }
  };

  const onSubmit = (data: TripFormData) => {
    const parsed = tripSchema.parse(data);
    createMutation.mutate(parsed);
  };

  const vehicleOptions = vehicles?.map((v) => ({
    value: v.id,
    label: `${v.registrationNumber} - ${v.make} ${v.model}`,
  })) || [];

  const driverOptions = drivers?.map((d) => ({
    value: d.id,
    label: d.fullName,
  })) || [];

  const routeOptions = routes?.map((r) => ({
    value: r.id,
    label: `${r.origin} → ${r.destination} (${r.officialDistanceKm} km)`,
  })) || [];

  const contractOptions = activeContracts?.map((c) => ({
    value: c.id,
    label: `${c.contractNumber} - ${c.contractName} (${c.clientName})`,
  })) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/trips')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-neutral-charcoal mb-2">Create New Trip</h1>
          <p className="text-gray-600">Fill in the trip details</p>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-charcoal mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Vehicle"
                options={vehicleOptions}
                error={errors.vehicleId?.message}
                {...register('vehicleId', { valueAsNumber: true })}
                required
              />
              <Select
                label="Driver"
                options={driverOptions}
                error={errors.driverId?.message}
                {...register('driverId', { valueAsNumber: true })}
                required
              />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-neutral-charcoal mb-4">Contract Linking (Optional)</h3>
            <div className="space-y-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={linkToContract}
                  onChange={(e) => {
                    setLinkToContract(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedContractId(null);
                      setValue('contractId', undefined);
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">Link this trip to a contract</span>
              </label>

              {linkToContract && (
                <>
                  <Select
                    label="Select Contract"
                    options={contractOptions}
                    onChange={(e) => handleContractChange(e.target.value)}
                    value={selectedContractId || ''}
                  />
                  
                  {selectedContract && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Contract Details</h4>
                      <div className="space-y-1 text-sm text-blue-800">
                        <div><strong>Contract:</strong> {selectedContract.contractNumber} - {selectedContract.contractName}</div>
                        <div><strong>Client:</strong> {selectedContract.clientName}</div>
                        <div><strong>Route:</strong> {selectedContract.origin} → {selectedContract.destination}</div>
                        <div><strong>Remaining Weight:</strong> {(selectedContract.totalWeightTons - selectedContract.completedWeightTons).toFixed(2)} tons</div>
                        <div><strong>Completion:</strong> {((selectedContract.completedWeightTons / selectedContract.totalWeightTons) * 100).toFixed(1)}%</div>
                        {selectedContract.ratePerTon && (
                          <div><strong>Rate per Ton:</strong> {new Intl.NumberFormat('en-TZ', { style: 'currency', currency: 'TZS', minimumFractionDigits: 0 }).format(selectedContract.ratePerTon)}</div>
                        )}
                      </div>
                      <p className="text-xs text-blue-600 mt-2">✓ Origin and destination have been auto-filled from contract</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-neutral-charcoal mb-4">Route Details</h3>
            <div className="space-y-4">
              <Select
                label="Select Route (Optional)"
                options={routeOptions}
                onChange={(e) => handleRouteChange(e.target.value)}
                value={selectedRouteId || ''}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <LocationAutocomplete
                  label="Origin"
                  value={origin || ''}
                  onChange={(value) => setValue('origin', value)}
                  onLocationSelect={handleOriginSelect}
                  error={errors.origin?.message}
                  placeholder="e.g., Dar es Salaam"
                  required
                />
                <LocationAutocomplete
                  label="Destination"
                  value={destination || ''}
                  onChange={(value) => setValue('destination', value)}
                  onLocationSelect={handleDestSelect}
                  error={errors.destination?.message}
                  placeholder="e.g., Dodoma"
                  required
                />
              </div>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={calculateDistance}
                  disabled={!originLocation || !destLocation || isCalculating}
                >
                  Calculate distance
                </Button>
              </div>

              {isCalculating && (
                <div className="text-sm text-primary flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                  Calculating distance...
                </div>
              )}

              <Input
                label="Planned Distance (km)"
                type="number"
                step="0.1"
                placeholder="e.g., 486"
                error={errors.plannedDistanceKm?.message}
                {...register('plannedDistanceKm', {
                  setValueAs: (v) => (v === '' || v === null ? undefined : Number(v)),
                })}
              />

              <Input
                label="Weight (Tons)"
                type="number"
                step="0.1"
                placeholder="e.g., 12.5"
                error={errors.weightInTons?.message}
                {...register('weightInTons', {
                  setValueAs: (v) => (v === '' || v === null ? undefined : Number(v)),
                })}
              />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-neutral-charcoal mb-4">Pricing Information</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-charcoal mb-2">Rate Type</label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="rateType"
                    checked={rateType === 'km'}
                    onChange={() => handleRateTypeChange('km')}
                    className="w-4 h-4 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700">Rate per KM</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="rateType"
                    checked={rateType === 'ton'}
                    onChange={() => handleRateTypeChange('ton')}
                    className="w-4 h-4 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700">Rate per Ton</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="rateType"
                    checked={rateType === 'fixed'}
                    onChange={() => handleRateTypeChange('fixed')}
                    className="w-4 h-4 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700">Fixed Price</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rateType === 'km' && (
                <Input
                  label="Rate Per KM (TZS)"
                  type="number"
                  step="0.01"
                  placeholder="e.g., 500"
                  error={errors.ratePerKm?.message}
                  {...register('ratePerKm', {
                    setValueAs: (v) => (v === '' || v === null ? undefined : Number(v)),
                  })}
                />
              )}
              {rateType === 'ton' && (
                <Input
                  label="Rate Per Ton (TZS)"
                  type="number"
                  step="0.01"
                  placeholder="e.g., 50000"
                  error={errors.ratePerTon?.message}
                  {...register('ratePerTon', {
                    setValueAs: (v) => (v === '' || v === null ? undefined : Number(v)),
                  })}
                />
              )}
              {rateType === 'fixed' && (
                <Input
                  label="Fixed Trip Price (TZS)"
                  type="number"
                  step="0.01"
                  placeholder="e.g., 500000"
                  error={errors.fixedPrice?.message}
                  {...register('fixedPrice', {
                    setValueAs: (v) => (v === '' || v === null ? undefined : Number(v)),
                  })}
                />
              )}
              {(rateType === 'km' || rateType === 'ton') && (
                <Input
                  label="Total Amount (TZS)"
                  type="number"
                  step="0.01"
                  placeholder="Auto-calculated or enter manually"
                  error={errors.amount?.message}
                  {...register('amount', {
                    setValueAs: (v) => (v === '' || v === null ? undefined : Number(v)),
                  })}
                />
              )}
            </div>
            {rateType === 'fixed' && (
              <p className="text-xs text-gray-500 mt-2">
                The fixed price is used to calculate the remaining balance after allowances and expenses.
              </p>
            )}
            {rateType === 'km' && ratePerKm && plannedDistanceKm ? (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900 font-medium">Pricing Breakdown:</p>
                <div className="mt-2 text-sm text-blue-800">
                  Distance: {Number(plannedDistanceKm)} km × {new Intl.NumberFormat('en-TZ', { style: 'currency', currency: 'TZS', minimumFractionDigits: 0 }).format(Number(ratePerKm))} = {new Intl.NumberFormat('en-TZ', { style: 'currency', currency: 'TZS', minimumFractionDigits: 0 }).format(Number(ratePerKm) * Number(plannedDistanceKm))}
                </div>
              </div>
            ) : null}
            {rateType === 'ton' && ratePerTon && weightInTons ? (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900 font-medium">Pricing Breakdown:</p>
                <div className="mt-2 text-sm text-blue-800">
                  Weight: {Number(weightInTons)} tons × {new Intl.NumberFormat('en-TZ', { style: 'currency', currency: 'TZS', minimumFractionDigits: 0 }).format(Number(ratePerTon))} = {new Intl.NumberFormat('en-TZ', { style: 'currency', currency: 'TZS', minimumFractionDigits: 0 }).format(Number(ratePerTon) * Number(weightInTons))}
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-neutral-charcoal mb-4">Trip Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-charcoal mb-1">
                  Purpose <span className="text-error">*</span>
                </label>
                <textarea
                  rows={3}
                  placeholder="Describe the purpose of this trip..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  {...register('purpose')}
                />
                {errors.purpose && (
                  <p className="mt-1 text-sm text-error">{errors.purpose.message}</p>
                )}
              </div>

              <Input
                label="Job Reference"
                placeholder="e.g., JOB-2024-001"
                error={errors.jobReference?.message}
                {...register('jobReference')}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Start Date"
                  type="date"
                  error={errors.startDate?.message}
                  {...register('startDate')}
                  required
                />
                <Input
                  label="End Date"
                  type="date"
                  error={errors.endDate?.message}
                  {...register('endDate')}
                />
              </div>
            </div>
          </div>

          {/* Trip Expenses Section */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-charcoal mb-4">Trip Expenses (Optional)</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add any initial expenses for this trip. These will be recorded as expenses linked to both the trip and the vehicle.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newExpenseCategory}
                    onChange={(e) => setNewExpenseCategory(e.target.value as ExpenseCategory)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  >
                    <optgroup label="Allowances">
                      <option value={ExpenseCategory.DRIVER_ALLOWANCE}>Driver Allowance</option>
                      <option value={ExpenseCategory.ACCOMMODATION}>Accommodation</option>
                      <option value={ExpenseCategory.MEALS}>Meals</option>
                      <option value={ExpenseCategory.FUEL_ADVANCE}>Fuel Advance</option>
                    </optgroup>
                    <optgroup label="Trip Expenses">
                      <option value={ExpenseCategory.FUEL}>Fuel</option>
                      <option value={ExpenseCategory.TOLL_ROAD}>Toll/Road</option>
                      <option value={ExpenseCategory.TOLLS}>Tolls</option>
                      <option value={ExpenseCategory.PARKING}>Parking</option>
                      <option value={ExpenseCategory.LOADING_OFFLOADING}>Loading/Offloading</option>
                      <option value={ExpenseCategory.WEIGHBRIDGE}>Weighbridge</option>
                      <option value={ExpenseCategory.BORDER_FEES}>Border Fees</option>
                      <option value={ExpenseCategory.REPAIR}>Repair</option>
                      <option value={ExpenseCategory.OTHER}>Other</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (TZS)</label>
                  <input
                    type="number"
                    value={newExpenseAmount}
                    onChange={(e) => setNewExpenseAmount(e.target.value)}
                    placeholder="e.g., 50000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor (Optional)</label>
                  <input
                    type="text"
                    value={newExpenseVendor}
                    onChange={(e) => setNewExpenseVendor(e.target.value)}
                    placeholder="e.g., Shell Station"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <Button type="button" variant="secondary" onClick={handleAddExpense} className="w-full">
                    <Plus size={16} className="mr-1" />
                    Add
                  </Button>
                </div>
              </div>
              <div className="mt-2">
                <input
                  type="text"
                  value={newExpenseDescription}
                  onChange={(e) => setNewExpenseDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
            </div>

            {pendingExpenses.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Category</th>
                      <th className="px-4 py-2 text-left">Amount</th>
                      <th className="px-4 py-2 text-left">Vendor</th>
                      <th className="px-4 py-2 text-left">Description</th>
                      <th className="px-4 py-2 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingExpenses.map((expense) => (
                      <tr key={expense.id} className="border-t">
                        <td className="px-4 py-2">{expenseCategoryLabels[expense.category]}</td>
                        <td className="px-4 py-2 font-medium">{formatCurrency(expense.amount)}</td>
                        <td className="px-4 py-2">{expense.vendor || '-'}</td>
                        <td className="px-4 py-2">{expense.description || '-'}</td>
                        <td className="px-4 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveExpense(expense.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-4 py-2 font-semibold">Total</td>
                      <td className="px-4 py-2 font-bold text-primary">
                        {formatCurrency(pendingExpenses.reduce((sum, e) => sum + e.amount, 0))}
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="ghost" onClick={() => navigate('/trips')}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={createMutation.isPending}>
              Create Trip
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
